// Add / edit an application: /pam/applications/new, and the Settings tab of
// /pam/applications/:id. Saves through POST /api/v1/idp/apps/save
// (internal/idp/apps.go SaveApp, samlapps.go normaliseSAML).
import React, { useMemo, useRef, useState } from "react";
import { Button, Checkbox, Input, InputNumber, Select, Switch, message } from "antd";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, FileCode2, FileUp, Server, ShieldCheck, Smartphone, TriangleAlert, XCircle } from "lucide-react";

import { PageHeader, PageShell } from "../../design-system/tableUi";
import { AN_COLORS, AN_FONT, AN_MONO } from "../../design-system/tokens";
import { saveApp } from "../../service/idpService";
import { FieldLabel, InlineValue, Note, PanelHead, card } from "../enduser/ssh-proxy/sshProxyUi";
import {
  APP_TYPES,
  ErrorText,
  HelpText,
  RotateSecretButton,
  SecretHint,
  SecretModal,
  UriList,
  appTypeKey,
  cleanUris,
  hasRotatableSecret,
  reportIdpError,
  uriListError,
  validLogoUrl,
} from "./appsUi";

const TYPE_ICON = { web: Server, spa: Smartphone, saml: FileCode2, mfa_api: ShieldCheck };
const OPTIONAL_SCOPES = ["profile", "email", "groups"];
const DEFAULT_ATTRS = { email: "email", name: "name", username: "username", groups: "groups" };
const ATTR_FIELDS = [
  ["email", "Email"],
  ["name", "Name"],
  ["username", "Username"],
  ["groups", "Groups"],
];
const NAMEID_FORMATS = [
  { value: "emailAddress", label: "emailAddress" },
  { value: "unspecified", label: "unspecified" },
  { value: "persistent", label: "persistent" },
];
const NAMEID_SOURCES = [
  { value: "email", label: "Email" },
  { value: "upn", label: "UPN" },
  { value: "user_id", label: "AuthNull user ID" },
];

// MFA API apps (the redirect flow), as internal/idp/apps.go: the session
// (the ID token's lifetime) lasts 300 to 604800 s, 8 hours by default; the
// methods are one or both of push and totp.
const SESSION_MIN = 300;
const SESSION_MAX = 7 * 24 * 3600;
const SESSION_DEFAULT = 8 * 3600;
const SESSION_UNITS = [
  { value: 86400, label: "days" },
  { value: 3600, label: "hours" },
  { value: 60, label: "minutes" },
];
const MFA_METHODS = [
  { value: "push", label: "Push", desc: "AuthNull Authenticator" },
  { value: "totp", label: "Code", desc: "authenticator app" },
];

// Seconds as a number and the largest unit that divides them evenly
// (28800 → 8 hours, 5400 → 90 minutes). Odd seconds are rounded to minutes.
const sessionFrom = (seconds) => {
  const s = Number(seconds) > 0 ? Number(seconds) : SESSION_DEFAULT;
  const unit = SESSION_UNITS.find((u) => s % u.value === 0);
  return unit ? { sessionValue: s / unit.value, sessionUnit: unit.value } : { sessionValue: Math.max(1, Math.round(s / 60)), sessionUnit: 60 };
};
const sessionSeconds = (d) => Math.round(Number(d.sessionValue) * d.sessionUnit);

const field = { marginBottom: 16 };
const section = { ...card, padding: 18 };

const draftFrom = (app) => {
  const o = app?.oidc;
  const s = app?.saml;
  return {
    type: appTypeKey(app),
    name: app?.name || "",
    logoUrl: app?.logoUrl || "",
    enabled: app ? !!app.enabled : true,
    redirectUris: o?.redirectUris?.length ? [...o.redirectUris] : [""],
    postLogoutRedirectUris: o?.postLogoutRedirectUris?.length ? [...o.postLogoutRedirectUris] : [""],
    scopes: o?.allowedScopes?.length ? o.allowedScopes.filter((x) => OPTIONAL_SCOPES.includes(x)) : ["profile", "email"],
    requirePkce: o ? !!o.requirePkce : true,
    metadata: "",
    entityId: s?.entityId || "",
    acsUrls: s?.acsUrls?.length ? [...s.acsUrls] : [""],
    nameIdFormat: s?.nameIdFormat || "emailAddress",
    nameIdSource: s?.nameIdSource || "email",
    // An existing app's missing attribute is one it does not send: shown empty.
    attributes: s ? Object.fromEntries(ATTR_FIELDS.map(([k]) => [k, s.attributes?.[k] || ""])) : { ...DEFAULT_ATTRS },
    ...sessionFrom(app?.sessionTtlSeconds),
    mfaMethods: app?.mfaMethods?.length ? MFA_METHODS.map((m) => m.value).filter((m) => app.mfaMethods.includes(m)) : ["push", "totp"],
  };
};

// The client-side copy of the API's rules. { field: message }.
const validate = (d) => {
  const e = {};
  const name = d.name.trim();
  if (!name) e.name = "Enter a name.";
  else if ([...name].length > 80) e.name = "At most 80 characters.";
  if (!validLogoUrl(d.logoUrl.trim())) e.logoUrl = "The logo is an https:// URL.";
  if (d.type === "web" || d.type === "spa") {
    const r = uriListError(d.redirectUris, { required: true });
    if (r) e.redirectUris = r;
    const l = uriListError(d.postLogoutRedirectUris, { what: "sign-out redirect URI" });
    if (l) e.postLogoutRedirectUris = l;
  }
  if (d.type === "mfa_api") {
    // Optional, as on the server: an app that only calls do-authenticationV4 has none.
    const r = uriListError(d.redirectUris);
    if (r) e.redirectUris = r;
    const n = Number(d.sessionValue);
    const secs = sessionSeconds(d);
    if (d.sessionValue === null || d.sessionValue === "" || !Number.isInteger(n) || n <= 0) e.session = "Enter a whole number.";
    else if (secs < SESSION_MIN || secs > SESSION_MAX) e.session = "From 5 minutes to 7 days.";
    if (!d.mfaMethods.length) e.mfaMethods = "Choose at least one method.";
  }
  if (d.type === "saml") {
    const hasMeta = !!d.metadata.trim();
    const entity = d.entityId.trim();
    if (!entity && !hasMeta) e.entityId = "Enter the app's entity ID, or import its metadata.";
    else if (entity.length > 1024 || /\s/.test(entity)) e.entityId = "The entity ID has no spaces (at most 1024 characters).";
    const a = uriListError(d.acsUrls, { required: !hasMeta, what: "ACS URL" });
    if (a) e.acsUrls = a;
    if (d.metadata.length > 1024 * 1024) e.metadata = "The metadata is larger than 1 MB.";
    const badAttr = ATTR_FIELDS.find(([k]) => {
      const v = d.attributes[k] || "";
      return v.length > 256 || /[<>"\r\n]/.test(v);
    });
    if (badAttr) e.attributes = `${badAttr[1]}: at most 256 characters, without < > or ".`;
  }
  return e;
};

// The save body: the common fields and only the block for the app's type.
const bodyFrom = (d, app) => {
  const t = APP_TYPES.find((x) => x.key === d.type);
  const body = {
    ...(app?.id ? { id: app.id } : {}),
    name: d.name.trim(),
    protocol: t.protocol,
    logoUrl: d.logoUrl.trim(),
    disabled: !d.enabled,
  };
  if (d.type === "web" || d.type === "spa") {
    // A backend app keeps the method it has (client_secret_post stays post).
    const prev = app?.oidc?.tokenEndpointAuthMethod;
    const method = d.type === "spa" ? "none" : prev && prev !== "none" ? prev : t.method;
    body.oidc = {
      tokenEndpointAuthMethod: method,
      redirectUris: cleanUris(d.redirectUris),
      postLogoutRedirectUris: cleanUris(d.postLogoutRedirectUris),
      allowedScopes: ["openid", ...OPTIONAL_SCOPES.filter((x) => d.scopes.includes(x))],
      requirePkce: d.type === "spa" ? true : d.requirePkce,
    };
  } else if (d.type === "saml") {
    body.saml = {
      entityId: d.entityId.trim(),
      acsUrls: cleanUris(d.acsUrls),
      nameIdFormat: d.nameIdFormat,
      nameIdSource: d.nameIdSource,
      // Empty = not sent: the server drops empty names.
      attributes: Object.fromEntries(ATTR_FIELDS.map(([k]) => [k, (d.attributes[k] || "").trim()])),
      ...(d.metadata.trim() ? { metadata: d.metadata } : {}),
    };
  }
  // mfa_api: a confidential client with the "mfa" scope; the server issues a
  // secret on create. It keeps its client_secret_* method (post stays post),
  // and never sends postLogoutRedirectUris (ErrAPIRedirect). The three
  // redirect-flow settings are always sent: a save without them keeps the
  // stored ones, so leaving them out could never clear the redirect URIs.
  if (d.type === "mfa_api") {
    const prev = app?.oidc?.tokenEndpointAuthMethod;
    body.oidc = {
      tokenEndpointAuthMethod: prev === "client_secret_basic" || prev === "client_secret_post" ? prev : t.method,
      redirectUris: cleanUris(d.redirectUris),
    };
    body.sessionTtlSeconds = sessionSeconds(d);
    body.mfaMethods = MFA_METHODS.map((m) => m.value).filter((m) => d.mfaMethods.includes(m));
  }
  return body;
};

function TypeCards({ value, onChange, isAllowed }) {
  return (
    // Two by two, so no card sits alone on a row; one column on a phone (index.css).
    <div className="an-app-type-grid" style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 10 }}>
      {APP_TYPES.map((t) => {
        const Icon = TYPE_ICON[t.key];
        const active = value === t.key;
        const allowed = isAllowed(t.key);
        return (
          <button
            key={t.key}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={!allowed}
            onClick={() => allowed && onChange(t.key)}
            style={{
              textAlign: "left",
              padding: 14,
              borderRadius: 10,
              border: `1px solid ${active ? AN_COLORS.primary : AN_COLORS.line}`,
              boxShadow: active ? `0 0 0 1px ${AN_COLORS.primary}` : "none",
              background: active ? AN_COLORS.primaryL : AN_COLORS.surface,
              cursor: allowed ? (active ? "default" : "pointer") : "not-allowed",
              opacity: allowed || active ? 1 : 0.5,
              fontFamily: AN_FONT,
              display: "grid",
              gap: 6,
              alignContent: "start",
            }}
          >
            <span
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: active ? AN_COLORS.primary : AN_COLORS.bg,
                color: active ? "white" : AN_COLORS.ink2,
              }}
            >
              <Icon size={16} />
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: AN_COLORS.ink }}>{t.title}</span>
            <span style={{ fontFamily: AN_MONO, fontSize: 10.5, letterSpacing: 0.6, textTransform: "uppercase", color: AN_COLORS.muted }}>
              {t.tech}
            </span>
            <span style={{ fontSize: 12, color: AN_COLORS.muted, lineHeight: 1.45 }}>{t.desc}</span>
          </button>
        );
      })}
    </div>
  );
}

// The rotate-secret card of a confidential client's settings: the secret's
// hint and creation date next to Rotate secret. onRotated reads the app again.
function SecretCard({ app, onRotated }) {
  return (
    <div style={section}>
      <PanelHead
        title="Client secret"
        desc="The app authenticates to AuthNull with its client ID and this secret. It was shown once, when it was made; AuthNull keeps only a hash."
        right={
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
            <SecretHint oidc={app.oidc} />
            <RotateSecretButton app={app} onRotated={onRotated} />
          </div>
        }
      />
    </div>
  );
}

/**
 * The form. `app` is null to create. `onSaved(app)` runs after a save — after
 * the secret dialog is closed, when one was issued. `onSecretRotated()` runs
 * after a rotation, to read the app again.
 */
export function ApplicationSettings({ app, onSaved, onCancel, onNotFound, onSecretRotated }) {
  const editing = !!app;
  const [msg, msgCtx] = message.useMessage();
  const [d, setD] = useState(() => draftFrom(app));
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [saving, setSaving] = useState(false);
  // Only while the dialog is open; cleared when it closes.
  const [secret, setSecret] = useState(null);
  const [savedApp, setSavedApp] = useState(null);
  const fileRef = useRef(null);

  const set = (patch) => {
    setD((prev) => ({ ...prev, ...patch }));
    setServerError("");
  };
  const oidc = d.type === "web" || d.type === "spa";
  const originalType = appTypeKey(app);
  // The protocol cannot change once created. The backend allows switching an
  // OpenID Connect app between backend and single-page (secret made / dropped).
  const isAllowed = (key) =>
    !editing || key === originalType || (["web", "spa"].includes(key) && ["web", "spa"].includes(originalType));

  const save = () => {
    const e = validate(d);
    setErrors(e);
    setServerError("");
    if (Object.keys(e).length) return;
    setSaving(true);
    saveApp(bodyFrom(d, app))
      .then((res) => {
        setSaving(false);
        const saved = res?.app;
        if (res?.clientSecret) {
          setSavedApp(saved);
          setSecret(res.clientSecret);
        } else {
          if (editing) msg.success("Saved.");
          onSaved?.(saved);
        }
      })
      .catch((err) => {
        setSaving(false);
        reportIdpError(err, { msg, fallback: "Could not save the application.", setInline: setServerError, onNotFound });
      });
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set({ metadata: String(reader.result || "") });
    reader.onerror = () => msg.error("Could not read the file.");
    reader.readAsText(file);
  };

  const switchedToSpa = editing && originalType === "web" && d.type === "spa";
  const switchedToWeb = editing && originalType === "spa" && d.type === "web";

  return (
    <div style={{ display: "grid", gap: 16, maxWidth: "56rem" }}>
      {msgCtx}
      <SecretModal
        secret={secret}
        clientId={savedApp?.oidc?.clientId}
        onDone={() => {
          setSecret(null);
          const next = savedApp;
          setSavedApp(null);
          onSaved?.(next);
        }}
      />

      <div style={section}>
        <PanelHead
          title="Application type"
          desc={editing ? "The type can't be changed after the app is created. Create a new app instead." : "Not sure? Look in your app's settings for 'SSO', 'SAML' or 'OpenID Connect'. If it has none and you can change its code, use MFA API."}
        />
        <TypeCards value={d.type} onChange={(type) => set({ type })} isAllowed={isAllowed} />
        {switchedToSpa && (
          <div style={{ marginTop: 12 }}>
            <Note tone="warn">A single-page or mobile app has no secret: saving removes this app's client secret.</Note>
          </div>
        )}
        {switchedToWeb && (
          <div style={{ marginTop: 12 }}>
            <Note tone="info">Saving issues a client secret for this app. It is shown once.</Note>
          </div>
        )}
      </div>

      <div style={section}>
        <PanelHead title="Details" />
        <div style={field}>
          <FieldLabel required>Name</FieldLabel>
          <Input
            value={d.name}
            maxLength={80}
            placeholder="e.g. ERP"
            status={errors.name ? "error" : undefined}
            onChange={(e) => set({ name: e.target.value })}
          />
          <ErrorText>{errors.name}</ErrorText>
        </div>
        <div style={field}>
          <FieldLabel hint="optional, https://">Logo URL</FieldLabel>
          <Input
            value={d.logoUrl}
            placeholder="https://app.example.com/logo.png"
            style={{ fontFamily: AN_MONO }}
            status={errors.logoUrl ? "error" : undefined}
            onChange={(e) => set({ logoUrl: e.target.value })}
          />
          <ErrorText>{errors.logoUrl}</ErrorText>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Switch checked={d.enabled} onChange={(enabled) => set({ enabled })} />
          <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>{d.enabled ? "Enabled" : "Disabled"}</span>
        </div>
        <HelpText>
          {d.type === "mfa_api"
            ? "When off, AuthNull refuses every request from this app."
            : "When off, assigned users can't sign in to this app."}
        </HelpText>
      </div>

      {oidc && (
        <div style={section}>
          <PanelHead title="OpenID Connect" />
          <div style={field}>
            <FieldLabel required>Redirect URIs</FieldLabel>
            <UriList
              value={d.redirectUris}
              onChange={(redirectUris) => set({ redirectUris })}
              placeholder="https://app.example.com/callback"
              addLabel="Add redirect URI"
              status={errors.redirectUris ? "error" : undefined}
            />
            <HelpText>
              Where AuthNull sends the user back after sign-in. Matched exactly, character for character: no
              wildcards (<InlineValue>*</InlineValue>) and no fragments (<InlineValue>#</InlineValue>).{" "}
              <InlineValue>https://</InlineValue> only; <InlineValue>http://localhost</InlineValue> is allowed for development.
            </HelpText>
            <ErrorText>{errors.redirectUris}</ErrorText>
          </div>
          <div style={field}>
            <FieldLabel hint="optional">Sign-out redirect URIs</FieldLabel>
            <UriList
              value={d.postLogoutRedirectUris}
              onChange={(postLogoutRedirectUris) => set({ postLogoutRedirectUris })}
              placeholder="https://app.example.com/signed-out"
              addLabel="Add sign-out URI"
              status={errors.postLogoutRedirectUris ? "error" : undefined}
            />
            <HelpText>Where the user may land after signing out. Same rules, matched exactly.</HelpText>
            <ErrorText>{errors.postLogoutRedirectUris}</ErrorText>
          </div>
          <div style={field}>
            <FieldLabel>Scopes the app may request</FieldLabel>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <Checkbox checked disabled>
                <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>openid</span>
              </Checkbox>
              {OPTIONAL_SCOPES.map((sc) => (
                <Checkbox
                  key={sc}
                  checked={d.scopes.includes(sc)}
                  onChange={(e) => set({ scopes: e.target.checked ? [...d.scopes, sc] : d.scopes.filter((x) => x !== sc) })}
                >
                  <span style={{ fontFamily: AN_MONO, fontSize: 12.5 }}>{sc}</span>
                </Checkbox>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Switch
              checked={d.type === "spa" ? true : d.requirePkce}
              disabled={d.type === "spa"}
              onChange={(requirePkce) => set({ requirePkce })}
            />
            <span style={{ fontSize: 13, color: AN_COLORS.ink2 }}>
              Require PKCE
              {d.type === "spa" && <span style={{ color: AN_COLORS.muted }}> (always on for single-page and mobile apps)</span>}
            </span>
          </div>
        </div>
      )}

      {d.type === "saml" && (
        <div style={section}>
          <PanelHead title="SAML" desc="SP-initiated sign-in, HTTP-POST binding. AuthNull signs both the response and the assertion." />

          <div style={field}>
            <FieldLabel hint="optional">Import from metadata</FieldLabel>
            <Input.TextArea
              value={d.metadata}
              onChange={(e) => set({ metadata: e.target.value })}
              placeholder='<md:EntityDescriptor entityID="https://app.example.com/saml" …>'
              autoSize={{ minRows: 3, maxRows: 10 }}
              spellCheck={false}
              status={errors.metadata ? "error" : undefined}
              style={{ fontFamily: AN_MONO, fontSize: 12 }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, flexWrap: "wrap" }}>
              <input
                ref={fileRef}
                type="file"
                accept=".xml,text/xml,application/xml,application/samlmetadata+xml"
                onChange={onFile}
                style={{ display: "none" }}
              />
              <Button size="small" icon={<FileUp size={13} style={{ verticalAlign: -2 }} />} onClick={() => fileRef.current?.click()}>
                Upload .xml
              </Button>
              {d.metadata && (
                <Button size="small" type="link" onClick={() => set({ metadata: "" })} style={{ padding: 0 }}>
                  Clear
                </Button>
              )}
            </div>
            <HelpText>
              Paste the app's SP metadata or upload its .xml file. When saved, AuthNull fills the Entity ID and the
              HTTP-POST ACS URLs from it if those fields are empty.
            </HelpText>
            <ErrorText>{errors.metadata}</ErrorText>
          </div>

          <div style={field}>
            <FieldLabel required={!d.metadata.trim()}>Entity ID</FieldLabel>
            <Input
              value={d.entityId}
              placeholder="https://app.example.com/saml"
              style={{ fontFamily: AN_MONO }}
              status={errors.entityId ? "error" : undefined}
              onChange={(e) => set({ entityId: e.target.value })}
            />
            <HelpText>The app's Issuer (its service provider entity ID). No spaces.</HelpText>
            <ErrorText>{errors.entityId}</ErrorText>
          </div>

          <div style={field}>
            <FieldLabel required={!d.metadata.trim()}>ACS URLs</FieldLabel>
            <UriList
              value={d.acsUrls}
              onChange={(acsUrls) => set({ acsUrls })}
              placeholder="https://app.example.com/saml/acs"
              addLabel="Add ACS URL"
              status={errors.acsUrls ? "error" : undefined}
            />
            <HelpText>
              Where the app receives the assertion (HTTP-POST binding only). Matched exactly; <InlineValue>https://</InlineValue>{" "}
              only, <InlineValue>http://localhost</InlineValue> for development; no <InlineValue>*</InlineValue> or{" "}
              <InlineValue>#</InlineValue>.
            </HelpText>
            <ErrorText>{errors.acsUrls}</ErrorText>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(14rem, 1fr))", gap: 12 }}>
            <div style={field}>
              <FieldLabel>NameID format</FieldLabel>
              <Select value={d.nameIdFormat} options={NAMEID_FORMATS} onChange={(nameIdFormat) => set({ nameIdFormat })} style={{ width: "100%" }} />
            </div>
            <div style={field}>
              <FieldLabel>NameID value</FieldLabel>
              <Select value={d.nameIdSource} options={NAMEID_SOURCES} onChange={(nameIdSource) => set({ nameIdSource })} style={{ width: "100%" }} />
              <HelpText>
                <InlineValue>user_id</InlineValue> stays the same even if the email changes.
              </HelpText>
            </div>
          </div>

          <div>
            <FieldLabel hint="empty = not sent">Attribute names</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(11rem, 1fr))", gap: 10 }}>
              {ATTR_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <div style={{ fontSize: 11.5, color: AN_COLORS.muted, marginBottom: 4 }}>{label}</div>
                  <Input
                    value={d.attributes[k]}
                    maxLength={256}
                    placeholder="not sent"
                    style={{ fontFamily: AN_MONO }}
                    status={errors.attributes ? "error" : undefined}
                    onChange={(e) => set({ attributes: { ...d.attributes, [k]: e.target.value } })}
                  />
                </div>
              ))}
            </div>
            <HelpText>The names of the attributes AuthNull puts in the assertion for the user's email, name, username and groups.</HelpText>
            <ErrorText>{errors.attributes}</ErrorText>
          </div>
        </div>
      )}

      {d.type === "mfa_api" && (
        <div style={section}>
          <PanelHead
            title="Sign-in with AuthNull"
            desc={`After its own password check, the app sends the user to AuthNull for the second factor and gets them back on its redirect URI.${editing ? "" : " A client secret is issued when you create the app, and shown once."}`}
          />
          <div style={field}>
            <FieldLabel>Redirect URIs</FieldLabel>
            <UriList
              value={d.redirectUris}
              onChange={(redirectUris) => set({ redirectUris })}
              placeholder="https://erp.example.com/authnull/callback"
              addLabel="Add redirect URI"
              status={errors.redirectUris ? "error" : undefined}
            />
            <HelpText>
              Where AuthNull sends the user back after the second factor: the app's callback. Matched exactly, character
              for character: no wildcards (<InlineValue>*</InlineValue>) and no fragments (<InlineValue>#</InlineValue>).{" "}
              <InlineValue>https://</InlineValue> only; <InlineValue>http://localhost</InlineValue> and{" "}
              <InlineValue>http://127.0.0.1</InlineValue> are allowed for development.
            </HelpText>
            <ErrorText>{errors.redirectUris}</ErrorText>
            {!errors.redirectUris && cleanUris(d.redirectUris).length === 0 && (
              <div style={{ marginTop: 8 }}>
                <Note tone="warn" icon={<TriangleAlert size={15} />}>
                  Without a redirect URI the app can't send its users to AuthNull for the second factor.
                </Note>
              </div>
            )}
          </div>
          <div style={field}>
            <FieldLabel required>Session length</FieldLabel>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <InputNumber
                value={d.sessionValue}
                min={1}
                precision={0}
                status={errors.session ? "error" : undefined}
                onChange={(sessionValue) => set({ sessionValue })}
                style={{ width: "8rem" }}
                aria-label="Session length"
              />
              <Select
                value={d.sessionUnit}
                options={[...SESSION_UNITS].reverse()}
                onChange={(sessionUnit) => set({ sessionUnit })}
                style={{ width: "8rem" }}
                aria-label="Unit"
              />
            </div>
            <HelpText>
              How long users stay signed in to the app after MFA (the ID token's lifetime). From 5 minutes to 7 days;
              8 hours by default.
            </HelpText>
            <ErrorText>{errors.session}</ErrorText>
          </div>
          <div>
            <FieldLabel required>MFA methods</FieldLabel>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {MFA_METHODS.map((m) => (
                <Checkbox
                  key={m.value}
                  checked={d.mfaMethods.includes(m.value)}
                  onChange={(e) =>
                    set({ mfaMethods: e.target.checked ? [...d.mfaMethods, m.value] : d.mfaMethods.filter((x) => x !== m.value) })
                  }
                >
                  {m.label} <span style={{ color: AN_COLORS.muted }}>({m.desc})</span>
                </Checkbox>
              ))}
            </div>
            <HelpText>What AuthNull's page offers. A method that is off is refused.</HelpText>
            <ErrorText>{errors.mfaMethods}</ErrorText>
          </div>
        </div>
      )}

      {editing && hasRotatableSecret(app) && originalType === d.type && <SecretCard app={app} onRotated={onSecretRotated} />}

      {serverError && (
        <Note tone="danger" icon={<XCircle size={15} />}>
          {serverError}
        </Note>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button type="primary" loading={saving} onClick={save}>
          {editing ? "Save changes" : "Add application"}
        </Button>
      </div>
    </div>
  );
}

// /pam/applications/new
export default function ApplicationForm() {
  const navigate = useNavigate();
  const back = useMemo(() => () => navigate("/pam/applications"), [navigate]);
  return (
    <PageShell>
      <PageHeader
        eyebrow="Applications"
        title="Add application"
        subtitle="Register an app your users sign in to through AuthNull, or that asks AuthNull for the second factor."
        actions={
          <Button onClick={back} icon={<ArrowLeft size={14} style={{ verticalAlign: -2 }} />}>
            Applications
          </Button>
        }
      />
      <ApplicationSettings
        app={null}
        onCancel={back}
        onSaved={(saved) =>
          saved?.id ? navigate(`/pam/applications/${saved.id}?tab=connect`, { replace: true }) : back()
        }
      />
    </PageShell>
  );
}
